import { useUserCompanyId, useUserRole } from "../state/user";
import { hasDownloadFeatureForTrees } from "../../modules/chartBuilder/AggregatesChart";
import { ChartSelect } from "../../modules/chartBuilder/useChartBuilderApiV2";
import { useCompanies } from "./useCompanies";
import { UserScopes } from "../../openapi-typescript/common/user_scopes";
import { CompanyFeatures } from "../../openapi-typescript/common/company_features";

type NoFeatureFlag = "noFeatureFlagRequired";
type TemporaryFeatureFlag =
  | "registry-viewer-csv"
  | "asset-intelligence-explorer-csv"
  | "asset-intelligence-asset-performance-csv"
  | "asset-intelligence-market-dynamics"
  | "asset-intelligence-beta-features";
export type FeatureFlag =
  | UserScopes
  | CompanyFeatures["feature"]
  | TemporaryFeatureFlag;

export interface Page {
  link: string;
  feature: FeatureFlag | NoFeatureFlag;
  name: string;
  icon: string;
  child?: Page[];
}

export const useDownloadCsvButtonFeature = (
  allSelections: ChartSelect[] | undefined
) => {
  const { sync, loading, features: featureList } = useFeatureFlags();
  const trees = allSelections?.map(({ tree, selection: { variable } }) => ({
    tree,
    variable,
  }));
  const isLoading = loading && !sync;
  return !isLoading && allSelections
    ? hasDownloadFeatureForTrees(trees, featureList)
    : false;
};

export const pathToFeature = (path: string) => path.replace("/", "").split("/");

export const useFeatureFlags = (
  specificCompanyId?: number
): {
  loading: boolean;
  sync: boolean;
  error?: Error | false;
  features: FeatureFlag[];
  hasFeature: (feature: FeatureFlag) => boolean;
  hasAnyFeature: (featureList: FeatureFlag[]) => boolean;
  getAvailableFeatures: (featureList: FeatureFlag[]) => FeatureFlag[];
} => {
  const isPendingRole = useUserRole() === "pending";
  const companyId = useUserCompanyId();
  const { loading, error, sync, updatable } = useCompanies(
    specificCompanyId ?? companyId
  );

  const scopes = updatable?.[0]?.data?.scopes?.map((scope) => {
    return scope.scope as FeatureFlag;
  });

  const features = updatable?.[0]?.data?.features?.map((feature) => {
    return feature.feature as FeatureFlag;
  });

  const data = [...(features ?? []), ...(scopes ?? [])];

  const activeFeatures = isPendingRole ? [] : data;

  const hasFeature = (feature: FeatureFlag) => activeFeatures.includes(feature);

  const getAvailableFeatures = (featureList: FeatureFlag[]) =>
    activeFeatures.filter((element) => featureList.includes(element));

  const hasAnyFeature = (featureList: FeatureFlag[]) =>
    getAvailableFeatures(featureList).length > 0;

  return {
    error: error,
    loading: loading,
    sync: sync,
    features: activeFeatures,
    hasFeature,
    hasAnyFeature,
    getAvailableFeatures,
  };
};

export const productScopesPattern = /^core\.product\.([a-zA-Z0-9_-]+)\.read$/;

export const useProductScopes = () => {
  const { error, loading, sync, features } = useFeatureFlags();
  const isLoading = loading && !sync;
  let scopes: FeatureFlag[] = [];
  if (!isLoading) {
    scopes = features.filter((item) => productScopesPattern.test(item));
  }
  return {
    error: error,
    loading: loading,
    sync: sync,
    scopes,
  };
};

export const useGetMenuPermission = (
  featureList: FeatureFlag[],
  companyId?: number
) => {
  /**
   * @deprecated Prefer useFeatureFlags() with hasFeature/hasAnyFeature/getAvailableFeatures.
   */
  const { sync, loading, error, hasAnyFeature, getAvailableFeatures } =
    useFeatureFlags(companyId);
  const availableFeatures = getAvailableFeatures(featureList);
  return {
    sync,
    loading,
    error,
    isHavePermission: hasAnyFeature(featureList),
    availableFeatures,
  };
};

const productTypeScopeMap: Record<string, string> = {
  mn: "morning",
  mp: "morning-peak",
  dy: "daytime",
  ep: "evening-peak",
  ev: "evening",
  nt: "nighttime",
  ht1h: "heads-and-tails-1h",
  ht2h: "heads-and-tails-2h",
  ht3h: "heads-and-tails-3h",
  ht4h: "heads-and-tails-4h",
  ht8h: "heads-and-tails-8h",
};

export const checkProductScopePermission = (
  productType: string,
  scopes: FeatureFlag[]
) => {
  const normalizedProductType = productType?.replace(/\s/g, "").toLowerCase();
  const mappedScope = productTypeScopeMap[normalizedProductType];
  const allowProductTypeList = scopes
    .map((item) => {
      const match = item.match(productScopesPattern);
      return match ? match[1] : null;
    })
    .filter(Boolean);
  if (mappedScope) {
    return allowProductTypeList.includes(mappedScope);
  }
  return allowProductTypeList.includes(normalizedProductType);
};
